import React, { useState, useMemo } from 'react';
import { useEmployeeMonitor, MonitoredEmployee } from '../../hooks/useEmployeeMonitor';
import './TeamDashboard.css';

type FilterType = 'all' | 'working' | 'not_tracking';
type GroupFilter = 'all' | 'elink' | 'phishingbox' | 'admin';
type SortType = 'name' | 'status' | 'idle_time' | 'today_total';

const TeamDashboard: React.FC = () => {
  const { 
    status, 
    dashboardStats, 
    loading, 
    initialCheckDone,
    error, 
    getEmployeeGroup,
    refresh, 
    startMonitor, 
    stopMonitor, 
    triggerCheck 
  } = useEmployeeMonitor(true, 30000);

  const [filter, setFilter] = useState<FilterType>('all');
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const [sortBy, setSortBy] = useState<SortType>('status');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and sort employees
  const filteredEmployees = useMemo(() => {
    if (!status?.employees || !Array.isArray(status.employees)) return [];

    let filtered = [...status.employees];

    // Apply group filter (eLink Design vs PhishingBox)
    if (groupFilter !== 'all') {
      filtered = filtered.filter(emp => getEmployeeGroup(emp.name) === groupFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.name.toLowerCase().includes(query) ||
        emp.currentTask?.projectName?.toLowerCase().includes(query) ||
        emp.currentTask?.description?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(emp => emp.status === filter);
    }

    // Apply sorting - working employees always at top first, then secondary sort
    const statusOrder: Record<string, number> = { working: 0, not_tracking: 1, error: 2, unknown: 3 };
    
    filtered.sort((a, b) => {
      // Primary sort by status (working first)
      const statusDiff = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      
      // Secondary sort based on selected option
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          // Already sorted by status, sort by name within same status
          return a.name.localeCompare(b.name);
        case 'idle_time':
          return (b.idleMinutes || 0) - (a.idleMinutes || 0);
        case 'today_total':
          // Parse duration strings like "2h 30m" for comparison
          const parseTime = (t: string | undefined) => {
            if (!t) return 0;
            const hours = t.match(/(\d+)h/)?.[1] || '0';
            const mins = t.match(/(\d+)m/)?.[1] || '0';
            return parseInt(hours) * 60 + parseInt(mins);
          };
          return parseTime(b.todayTotal) - parseTime(a.todayTotal);
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [status?.employees, filter, groupFilter, sortBy, searchQuery, getEmployeeGroup]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'working':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        );
      case 'not_tracking':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
          </svg>
        );
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'working': return 'Working';
      case 'not_tracking': return 'Not Tracking';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  const formatLastCheck = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  // Show loading state during initial check
  if (!initialCheckDone || (loading && !status?.employees?.length)) {
    return (
      <div className="team-dashboard">
        <div className="team-loading">
          <div className="spinner"></div>
          <span>{!initialCheckDone ? 'Running initial employee check...' : 'Loading team status...'}</span>
          <p className="loading-hint">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-dashboard">
      {/* Header */}
      <div className="team-header">
        <div className="team-title-section">
          <h1>Team Dashboard</h1>
          <p className="team-subtitle">Real-time employee monitoring</p>
        </div>
        <div className="team-controls">
          <button 
            className="control-btn refresh"
            onClick={refresh}
            disabled={loading}
            title="Refresh data"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            className={`control-btn trigger ${loading ? 'checking' : ''}`}
            onClick={triggerCheck}
            disabled={loading}
            title="Force a full check of all employees"
          >
            {loading ? (
              <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="32" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            )}
            {loading ? 'Checking...' : 'Run Check'}
          </button>
          {status?.isRunning ? (
            <button 
              className="control-btn stop"
              onClick={stopMonitor}
              title="Stop monitoring"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="6" y="4" width="4" height="16"></rect>
                <rect x="14" y="4" width="4" height="16"></rect>
              </svg>
              Stop Monitor
            </button>
          ) : (
            <button 
              className="control-btn start"
              onClick={startMonitor}
              title="Start monitoring"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              Start Monitor
            </button>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="team-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Project Breakdown */}
      {dashboardStats?.projectBreakdown && Object.keys(dashboardStats.projectBreakdown).length > 0 && (
        <div className="project-breakdown-section">
          <h3>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
            Active Projects
          </h3>
          <div className="project-breakdown-grid">
            {Object.entries(dashboardStats.projectBreakdown).map(([project, count]) => (
              <div key={project} className="project-item">
                <span className="project-name">{project}</span>
                <span className="project-count">{count} {count === 1 ? 'person' : 'people'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="team-filters">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search by name, project, or task..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({status?.employeeCount || 0})
          </button>
          <button 
            className={`filter-btn working ${filter === 'working' ? 'active' : ''}`}
            onClick={() => setFilter('working')}
          >
            Working ({status?.summary.working || 0})
          </button>
          <button 
            className={`filter-btn not-tracking ${filter === 'not_tracking' ? 'active' : ''}`}
            onClick={() => setFilter('not_tracking')}
          >
            Not Tracking ({status?.summary.notTracking || 0})
          </button>
        </div>

        <div className="group-filter">
          <span className="group-label">Team:</span>
          <button 
            className={`group-btn ${groupFilter === 'all' ? 'active' : ''}`}
            onClick={() => setGroupFilter('all')}
          >
            All Teams
          </button>
          <button 
            className={`group-btn admin ${groupFilter === 'admin' ? 'active' : ''}`}
            onClick={() => setGroupFilter('admin')}
          >
            Admin
          </button>
          <button 
            className={`group-btn elink ${groupFilter === 'elink' ? 'active' : ''}`}
            onClick={() => setGroupFilter('elink')}
          >
            eLink Design
          </button>
          <button 
            className={`group-btn phishingbox ${groupFilter === 'phishingbox' ? 'active' : ''}`}
            onClick={() => setGroupFilter('phishingbox')}
          >
            PhishingBox
          </button>
        </div>

        <div className="sort-select">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortType)}>
            <option value="status">Status</option>
            <option value="name">Name</option>
            <option value="idle_time">Idle Time</option>
            <option value="today_total">Today's Total</option>
          </select>
        </div>
      </div>

      {/* Employee Grid */}
      <div className="employee-grid">
        {filteredEmployees.length === 0 ? (
          <div className="no-employees">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <p>No employees found</p>
            {!status?.hasSession ? (
              <span>Waiting for session... Click "Run Check" after logging in</span>
            ) : !status?.isRunning ? (
              <span>Start the monitor to begin tracking</span>
            ) : !status?.lastCheckTime ? (
              <span>Click "Run Check" to fetch employee data</span>
            ) : (
              <span>No employees match your current filters</span>
            )}
          </div>
        ) : (
          filteredEmployees.map((employee) => (
            <EmployeeCard key={employee.employeeId} employee={employee} />
          ))
        )}
      </div>
    </div>
  );
};

// Employee Card Component
const EmployeeCard: React.FC<{ employee: MonitoredEmployee }> = ({ employee }) => {
  const getStatusClass = () => {
    switch (employee.status) {
      case 'working': return 'status-working';
      case 'not_tracking': return 'status-not-tracking';
      default: return 'status-unknown';
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className={`employee-card ${getStatusClass()}`}>
      <div className="employee-top-row">
        <div className="employee-avatar">
          <span className="avatar-initials">{getInitials(employee.name)}</span>
          <span className={`status-dot ${employee.status}`}></span>
        </div>
        <div className="employee-name-status">
          <span className="employee-name">{employee.name}</span>
          <span className={`employee-status ${employee.status}`}>
            {employee.status === 'working' && 'Working'}
            {employee.status === 'not_tracking' && 'Not Tracking'}
            {employee.status === 'error' && 'Error'}
            {employee.status === 'unknown' && 'Unknown'}
          </span>
        </div>
      </div>

      {employee.currentTask && (
        <div className="employee-task">
          <div className="task-project">{employee.currentTask.projectName}</div>
          <div className="task-description">{employee.currentTask.description}</div>
        </div>
      )}

      <div className="employee-footer">
        {employee.status === 'working' ? (
          <div className="task-time running">
            <span className="running-indicator"></span>
            <span className="value">Running</span>
          </div>
        ) : (
          <div className="task-time">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span className="value">Not tracking</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDashboard;

